(ns app.front.search-result
  (:require [app.domain.branding.model :as branding-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.target-time.model :as target-time-model]
            [app.front.ui.messages-panel :as messages-panel]
            [app.front.ui.select :as ui-select]
            [app.front.ui.updated :as ui-updated]
            [app.front.view.workspace.header :as ws-header]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [reagent.core :as r]
            [reitit.frontend.easy :as rfe]))


(defn conversation-link [{:keys [unseen-messages?] :as workspace}]
  [:span.px-0.text-right
   (cc/link
     {:href       "#"
      :on-click   #(do
                     (.stopPropagation %)
                     (.preventDefault %)
                     (>evt [::messages-panel/toggle-open (:workspace/id workspace)]))
      :class-name "text-ui-black hover:text-ui-violet-100"}
     (cc/icon {:icon        i/conversation
               :description "conversations"
               :style       {:fill "currentColor"}})
     (when unseen-messages?
       [:div.cloudpermit--notification-badge
        {:style {:right "0" :top "0" :margin-top "-3px" :height "10px" :min-width "10px"}}]))])


(defn email-notifications [result tr]
  (r/with-let [notifications-disabled? (r/atom (:notifications-disabled? result))]
    [:button.border-0
     {:on-click (fn [_]
                  (>evt [::ws-header/email-subscription result @notifications-disabled?])
                  (swap! notifications-disabled? not))}
     (cc/icon {:icon        (if @notifications-disabled? i/envelope-off i/envelope)
               :description (tr [(if @notifications-disabled? :search/enable-email :search/disable-email)])})]))


(defn middle-grouping-line
  "The lines in the middle section of the card use a smaller font and thus need a bigger margin
  to match the spacing within the other groups"
  [& children]
  (into [:div.smaller.mt-2] children))


(defn next-inspection-date [tr es-workspace]
  (when (-> es-workspace :workspace/permit-type #{:permit-type/CE})
    (when-let [date (:next-inspection-date es-workspace)]
      [middle-grouping-line
       [:span (str (tr [:inspection/next-inspection-date]) ": ")]
       [:span.bold ((<sub [::date-util/date-fn]) date)]])))


(defn case-priority [tr es-workspace]
  (when (-> es-workspace :workspace/permit-type #{:permit-type/CE})
    (let [priority (or (:workspace/case-priority es-workspace)
                       :case.priority/normal)]
      [middle-grouping-line
       [:span (str (tr [:case/priority]) ": ")]
       [:span.bold (tr [priority])]])))


(defn review-due-date
  "Shown on the card only when the results are sorted by review due date, so the user can
  see the upcoming review data driving the sort."
  [tr es-workspace]
  (let [date-fn          (<sub [::date-util/date-fn])
        overdue-count    (:review/overdue-count es-workspace)
        next-due-summary (:review/next-due-summary es-workspace)
        reviewer-name    (some-> (:review/reviewer-name next-due-summary)
                                 (#(if (keyword? %) (tr [%]) %)))
        next-due-date    (:review/due-date next-due-summary)]
    (when (or (pos? overdue-count) next-due-date)
      [:<>
       (when (pos? overdue-count)
         [middle-grouping-line
          [:span (str (tr [:search/overdue-reviews]) ": ")]
          [:span.bold overdue-count]])
       (when next-due-date
         [:<>
          [middle-grouping-line
           [:span (str (tr [:search/next-upcoming-review-due-date]) ": ")]
           [:span.bold (date-fn next-due-date)]]
          (when reviewer-name
            [middle-grouping-line
             [:span (str (tr [:review/reviewer]) ": ")]
             [:span.bold reviewer-name]])])])))


(defn workspace-state [tr es-workspace]
  [:div.flex.flex-row.items-center.gap-1.state-wrapper.mt-12.pr-2
   [:span  {:data-cy "workspace-state"
            :class   (str "bold uppercase flex-1  "
                          (ws-util/workspace-emphasis (:phase-state es-workspace)))}
    (ws-util/status-text (ws-util/workspace-state tr es-workspace) es-workspace)]
   (when-not (<sub [:session/authority-user?])
     [email-notifications es-workspace tr])
   [conversation-link es-workspace]])

(defn workspace->days-remaining
  [es-workspace]
  (when-let [end-date (:application/target-time-date es-workspace)]
    (-> {:start-date (date-util/start-of-day (js/Date.))
         :end-date   (date-util/start-of-day end-date)
         :unit       :target-time.unit/calendar-days}
        target-time-model/count-due-days)))

(defn workspace-show-target-time? [{:application/keys [target-time-state target-time-date target-time-paused?] :as es-workspace}]
  (case target-time-state
    ;; waiting for appeal is a special case where counter runs to zero, then disappers and pause is ignored
    :target-time/waiting-for-appeal (pos-int? (workspace->days-remaining es-workspace))
    ;; normally, show target time if either paused or has a target time
    (or target-time-paused? (some? target-time-date))))

(defn resolve-border-color
  [es-workspace]
  (when-let [days (and (workspace-show-target-time? es-workspace)
                       (workspace->days-remaining es-workspace))]
    (cond
      (< days 1)    "error"
      (<= 1 days 9) "warning")))

(defn resolve-licence-border-color [{valid-to :licence/valid-to}]
  (when valid-to
    (let [days (-> {:start-date (date-util/start-of-day (js/Date.))
                    :end-date   (date-util/start-of-day valid-to)
                    :unit       :target-time.unit/calendar-days}
                   target-time-model/count-due-days)]
      (cond
        (< days 0)  "error"
        (< days 30) "warning"))))

(defn due-date-indicator
  [tr es-workspace {:keys [compact?]}]
  [:div.flex.flex-1.rounded.bg-ui-white.text-ui-night-sky-100.items-center.justify-center
   {:class (if compact?
             "min-h-6 px-2 py-1 text-center"
             "m-2 h-10")}
   (if (:application/target-time-paused? es-workspace)
     (tr [:search/due-date-paused])
     (let [days (workspace->days-remaining es-workspace)]
       (apply tr (cond
                   (> 0 days) [[:search/due-date-overdue] [(- days)]]
                   (= 0 days) [[:search/due-date-today]]
                   (= 1 days) [[:search/due-date-tomorrow]]
                   :else      [[:search/due-date-in-days] [days]]))))])


(defn search-origin-query []
  (when (and (ff/enabled? :feature-flag.id/workspace-search-updates)
             (= :view/search (<sub [:route/name])))
    {:origin "search"}))


(defn workspace-link
  [tr {:keys [:workspace/id :group/uri-path-id phase-state has-pre-consultation?]}
   {:keys [class compact?] :or {class ""}}]
  (let [workspace   {:workspace/id          id
                     :group/uri-path-id     uri-path-id
                     :phase-state           phase-state
                     :has-pre-consultation? has-pre-consultation?}
        description (ws-util/workspace-link-description workspace)]
    [:a {:href       (ws-util/workspace-or-resume-application-href
                       workspace (ws-util/workspace-href workspace) (search-origin-query))
         :id         (str "app-link-" id)
         :data-cy    "open-workspace-btn"
         :class-name (str class
                          (if compact?
                            " min-h-6 px-2 py-1 rounded font-normal"
                            " h-10 font-bold")
                          " flex bg-ui-violet-100 text-white text-center items-center"
                          " no-underline hover:bg-ui-violet-130 focus:text-ui-night-sky-100 focus:bg-ui-violet-20")
         :aria-label (tr [description])}
     [:div.flex-1.uppercase (tr [description])]]))


(defn workspace-link-with-due-date
  [tr es-workspace {:keys [class compact?]}]
  (let [actions [:div.flex
                 {:class (when compact? ["gap-2 items-stretch" class])}
                 [workspace-link tr es-workspace {:class    (if compact?
                                                              "flex-1"
                                                              "flex-1 rounded m-2")
                                                  :compact? compact?}]
                 [due-date-indicator tr es-workspace {:compact? compact?}]]]
    (if compact?
      actions
      [:div.flex-1.content-wrapper.flex-col
       {:class [(resolve-border-color es-workspace) class]}
       actions])))


(defn workspace-link-section
  [tr es-workspace opts]
  (if (workspace-show-target-time? es-workspace)
    [workspace-link-with-due-date tr es-workspace opts]
    [workspace-link tr es-workspace opts]))


(defn search-result-box [{:keys [applicants
                                 property-owners
                                 project-id
                                 :workspace/permit-type
                                 :workspace/category-names
                                 :application/work-target-names
                                 loading?
                                 :group/uri-path-id]
                          :as   es-workspace}
                         {:keys [map-search? show-review-due-date? compact-action?]}
                         & footer]
  (let [tr                (<sub [:tr])
        lang              (<sub [:language])
        permit-type-label (ws-util/permit-type-label tr lang es-workspace)]
    [:div {:class (if (or map-search? compact-action?)
                    "w-full h-full"
                    "bx--col-xs-12 bx--col-sm-12 bx--col-md-6 bx--col-xl-4 bx--col-xxl-3 mt-4")}
     (cc/tile
       {:class-name (if compact-action?
                      "search-result-box rounded overflow-hidden"
                      "search-result-box")}

       [:div.content-wrapper.flex.flex-col
        {:class [(resolve-border-color es-workspace)
                 (when map-search? "!h-auto flex-1 min-h-0 overflow-y-auto")]}
        ;; Project name as link to the project
        (cc/link
          #_{:clj-kondo/ignore [:discouraged-var]}
          {:href       (rfe/href :view/project
                                 {:branding   (branding-model/or-default uri-path-id)
                                  :project-id project-id})
           :data-cy    "project-link"
           :class-name "font-bold"
           :aria-label (tr [:search/open-application])}
          (-> es-workspace :project/name))

        ;; Address
        [:div
         (when (seq (:property/id es-workspace))
           [:div.mt-1 (ws-util/search-result-address tr lang es-workspace)])
         [:div.mt-1 (municipality-model/common-name-province es-workspace tr lang)]]

        ;; Workspace work target and permit type
        [:p.mt-1
         (let [cat-names->str-fn        (fn [lang category-names]
                                          (->> category-names
                                               (map vector)
                                               (sort (i18n-model/localized-string-comparator lang))
                                               (map #(i18n-model/get-text % lang))
                                               (str/join ", ")))
               cat-names-in-lang-fn     (fn [lang category-names]
                                          (->> category-names
                                               (filter #(= (:entity/language %) lang))
                                               seq))
               fallback-lang            (->> category-names first :entity/language)
               cat-names-in-chosen-lang (or (cat-names-in-lang-fn lang category-names)
                                            (cat-names-in-lang-fn i18n-model/default-lang category-names)
                                            (cat-names-in-lang-fn fallback-lang category-names))
               cat-names-str            (cat-names->str-fn (-> cat-names-in-chosen-lang
                                                               first
                                                               :entity/language
                                                               (or lang))
                                                           cat-names-in-chosen-lang)]
           (cond
             category-names
             (str cat-names-str
                  (when-let [wt-text (some-> work-target-names (i18n-model/get-text lang))]
                    (str " - " wt-text))
                  " (" permit-type-label ")")

             permit-type
             permit-type-label

             :else
             ""))]

        (when loading?
          (cc/inline-loading {:description (tr [:search/loading-additional-info])}))
        (if-let [tasks (and (not show-review-due-date?)
                            (seq (:tasks es-workspace)))]
          ;; Task assignees, due dates
          (let [assignees (->> tasks
                               (mapcat :task/assignees)
                               distinct)]
            [:div.smaller
             [:div.bx--row
              [:span.mb-1.bx--col-xs-4
               (tr [(if (-> assignees
                            count
                            (= 1))
                      :assignee/assignee
                      :assignee/assignees)])]
              [:span.mb-1.bx--col-xs-8
               (->> assignees
                    sort
                    (str/join ", "))]]
             (when-let [dues (seq (keep :task/due tasks))]
               [:div.bx--row
                [:span.mb-1.bx--col-xs-4
                 (tr [:task/due])]
                [:span.mb-1.bx--col-xs-8
                 (->> dues
                      sort
                      (map (<sub [::date-util/date-fn]))
                      (str/join ", "))]])])
          ;; Applications applicant(s) and owner(s), not shown to stakeholders that have tasks in application
          [:div.smaller
           (when (and (seq applicants) (not show-review-due-date?))
             [:div.bx--row
              [:span.mb-1.bx--col-xs-4
               (if (> (count applicants) 1) (tr [:search/applicants]) (tr [:search/applicant]))]
              [:span.mb-1.bx--col-xs-8
               (str/join ", " applicants)]])
           (when (and (seq property-owners) (not show-review-due-date?))
             [:div.bx--row
              [:span.mb-1.bx--col-xs-4
               (if (> (count property-owners) 1) (tr [:search/owners]) (tr [:search/owner]))]
              [:span.mb-1.bx--col-xs-8
               (str/join ", " property-owners)]])
           (when (:workspace/assignee es-workspace)
             [:div.bx--row
              [:span.mb-1.bx--col-xs-4
               (tr [:search/assignee])]
              [:span.mb-1.bx--col-xs-8
               (:workspace/assignee es-workspace)]])])

        ;; Application updated ts
        [:div.smaller.mt-1
         [ui-updated/updated {:ts   (:modified es-workspace)
                              :user (:modified-by es-workspace)}]]

        [:div.flex-1]

        [next-inspection-date tr es-workspace]

        [case-priority tr es-workspace]

        (when (and show-review-due-date? (not map-search?))
          [review-due-date tr es-workspace])

        [workspace-state tr es-workspace]

        ;; footer
        (into [:div.flex.flex-row.justify-between.items-end {:class "min-h-[14px]"}]
              footer)
        (when compact-action?
          [:div.mt-4
           [workspace-link-section tr es-workspace {:compact? true}]])]

       (when-not compact-action?
         [workspace-link-section tr es-workspace
          {:class (when map-search? "rounded-bl rounded-br flex-none [&.content-wrapper]:!h-auto")}]))]))


(defn dashboard-result-footer [workspace]
  [:<>
   (when-let [case-id (-> workspace :workspace/municipal-case-id)]
     [:span case-id])])

(defn licence-link
  [tr {group-id :group/id licence-id :licence/id}]
  (cc/link
    {:href       (href/href :view/licence
                            {:group-id   group-id
                             :licence-id licence-id})
     :id         (str "app-link-" group-id "-" licence-id)
     :data-cy    "open-workspace-btn"
     :class-name "flex h-10 bg-ui-violet-100 text-white text-center font-bold items-center hover:no-underline"}
    [:div.flex-1.text-white.uppercase (tr [:search/open-licence])]))

(defn licence-search-result-box [{:keys [:licence/id
                                         :licence/valid-to
                                         :licence/valid-from
                                         :domain/state
                                         :group/tz
                                         category-names
                                         address
                                         has-open-bills?
                                         has-open-inspections?
                                         has-in-progress-application?] :as licence}
                                 map-search?]
  (let [tr          (<sub [:tr])
        lang        (<sub [:language])
        format-date (<sub [::date-util/date-fn])]
    [:div {:class (if map-search?
                    "w-full h-full"
                    "bx--col-xs-12 bx--col-sm-12 bx--col-md-6 bx--col-xl-4 bx--col-xxl-3 mt-4")}
     (cc/tile
       {:class-name "search-result-box"}

       [:div.content-wrapper
        {:class (resolve-licence-border-color licence)}
        (cc/link
          {:href       (href/href :view/licence
                                  {:group-id   (:group/id licence)
                                   :licence-id (:licence/id licence)})
           :data-cy    "project-link"
           :class-name "font-bold"}
          id)

        [:div.mb-5
         [:div.my-1
          (i18n-model/get-text (:holder-names licence) lang)]
         [:div.my-1
          (i18n-model/get-text category-names lang)]]

        [:div.mb-5
         (when address
           [:div.mt-1 address])
         [:div.mt-1 (municipality-model/common-name-province licence tr lang)]]

        [:div.smaller.mt-1
         [ui-updated/updated {:ts   (:modified licence)
                              :user (:modified-by licence)}]]

        (when valid-from
          [middle-grouping-line
           [:span (str (tr [:licence/valid-from]) ": ")]
           [:span.bold (format-date (date-util/to-tz valid-from tz))]])

        (when valid-to
          [middle-grouping-line
           [:span (str (tr [:licence/valid-to]) ": ")]
           [:span.bold (format-date (date-util/to-tz valid-to tz))]])

        [:div.mt-5 (tr [state])]

        (when has-open-bills?
          [:div.mt-1 (tr [:has-open-bills?])])
        (when has-open-inspections?
          [:div.mt-1 (tr [:has-open-inspections?])])
        (when (and has-in-progress-application?
                   (ff/enabled? :feature-flag.id/licence-search-updates))
          [:div.mt-1 (tr [:has-in-progress-application?])])]

       [licence-link tr licence])]))

(defn property-link
  [tr {db-id :id}]
  (cc/link
    {:href       (href/href :view/property {:db-id db-id})
     :id         (str "property-link-" db-id)
     :data-cy    "open-property-btn"
     :class-name "flex h-10 bg-ui-violet-100 text-white text-center font-bold items-center hover:no-underline"}
    [:div.flex-1.text-white.uppercase (tr [:search/open-property])]))

(defn property-search-result-box
  "Elasticsearch property rendered as a search result, e.g. next to the property search map."
  [{:property/keys [id primary-address] :domain/keys [state] db-id :id :as property} map-search?]
  (let [tr   (<sub [:tr])
        lang (<sub [:language])]
    [:div {:class (if map-search?
                    "w-full h-full"
                    "bx--col-xs-12 bx--col-sm-12 bx--col-md-6 bx--col-xl-4 bx--col-xxl-3 mt-4")}
     (cc/tile
       {:class-name "search-result-box"}

       [:div.content-wrapper
        (cc/link
          {:href       (href/href :view/property {:db-id db-id})
           :data-cy    "property-link"
           :class-name "font-bold"}
          id)

        [:div.mb-5
         [:div.mt-1 (or primary-address (tr [:address/address-missing]))]
         [:div.mt-1 (i18n-model/get-text (:municipality/names property) lang)]]

        [:div.smaller.mt-1
         [ui-updated/updated {:ts   (:modified property)
                              :user (:modified-by property)}]]

        [:div.mt-5 (tr [state])]]

       [property-link tr property])]))

(defn search-result-sort [{:keys [tr on-change value sort-options]}]
  [ui-select/select
   {:id           "search-sort-condition-select"
    :label-text   (tr [:dashboard-search/sort])
    :hide-label   true
    :allow-empty? false
    :values       sort-options
    :on-change    on-change
    :value        value
    :val->str     (fn [[attr direction]]
                    (tr [(keyword "search-sorting" (str (name attr) "-" (name direction)))]))}])
